// eslint-disable-next-line no-restricted-imports
import type { FieldError, FieldPath, FieldValues } from 'react-hook-form';

import type { ObjectDotNotation, ObjectDotNotationValue } from '@luna/utils-ts';

export const SERVER_ERROR_PATH = 'serverError';
type ServerErrorPath = typeof SERVER_ERROR_PATH;
export type ServerErrorValues = Record<ServerErrorPath, string>;

export type TypedFieldName<FormValue> = FormValue extends void
    ? string
    : FormValue extends FormValue[keyof FormValue] // handles enum case, see test
      ? string
      : ObjectDotNotation<FormValue>;

export type TypedFieldNameValue<FormValue, Path> =
    Path extends ObjectDotNotation<FormValue> ? ObjectDotNotationValue<FormValue, Path> : FormValue;

export type SetErrors<FormValue> = (
    errors: Partial<Record<TypedFieldName<FormValue> | ServerErrorPath, string>>,
) => void;

export interface ErrorDetails {
    errorType: FieldError['type'];
    message: string;
    shouldFocus: boolean;
}

export type SetError<FormValue extends FieldValues> = (
    fieldName: FieldPath<FormValue>,
    errorDetails: ErrorDetails,
) => void;

export type KeepStateOptions = Partial<{
    keepDirtyValues: boolean;
    keepErrors: boolean;
    keepDirty: boolean;
    keepValues: boolean;
    keepDefaultValues: boolean;
    keepIsSubmitted: boolean;
    keepIsSubmitSuccessful: boolean;
    keepTouched: boolean;
    keepIsValid: boolean;
    keepSubmitCount: boolean;
}>;

export type Reset<FormValues> = (values?: FormValues, keepStateOptions?: KeepStateOptions) => void;

export type TriggerFormValidation<FormValues> = (
    path?: ObjectDotNotation<FormValues> | ObjectDotNotation<FormValues>[],
) => Promise<boolean>;

export type SubmitHelpers<FormValue> = {
    setErrors: SetErrors<FormValue>;
    reset: Reset<FormValue>;
};

export type ClearErrors<FormValues> = (
    name?: ObjectDotNotation<FormValues> | ObjectDotNotation<FormValues>[],
) => void;

export type SetValueConfig = Partial<{
    shouldValidate: boolean;
    shouldDirty: boolean;
    shouldTouch: boolean;
}>;

export type SetValue<FormValues> = (
    path: ObjectDotNotation<FormValues> | string,
    value: unknown,
    options?: SetValueConfig,
) => void;

export type SetValues<FormValues> = (values: Partial<FormValues>, options?: SetValueConfig) => void;

export type SubmitHandler<FormValue> = (
    values: FormValue,
    helpers: SubmitHelpers<FormValue>,
) => Promise<void> | void;
